import React, { useState, useEffect, useCallback } from "react";
import {
  Select,
  Upload,
  Button,
  message,
  Space,
  Card,
  Typography,
  Table,
  Spin,
  Dropdown,
  Menu,
} from "antd";
import { InboxOutlined, PlusOutlined, FilterOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import moment from "moment";
import {
  addLeads,
  getLeads,
  getUserListDetails,
} from "../../../../Service/user";

const { Option } = Select;
const { Dragger } = Upload;
const { Title, Text } = Typography;

const UploadLeadsForm = ({ onUploadSuccess, showBackButton, onBack }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleRoleChange = (value) => {
    setSelectedRole(value);
  };

  const draggerProps = {
    name: "file",
    multiple: false,
    accept: ".csv",
    fileList: fileList,
    beforeUpload: (file) => {
      const isCsv = file.type === "text/csv" || file.name.endsWith(".csv");
      if (!isCsv) {
        message.error("You can only upload CSV files!");
        setFileList([]);
        setCsvData([]);
        return Upload.LIST_IGNORE;
      }
      setLoading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          Papa.parse(e.target.result, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              setLoading(false);
              if (results.errors.length > 0) {
                console.error("CSV Parsing Errors:", results.errors);
                message.error(
                  "Error parsing CSV file. Check console for details."
                );
                setCsvData([]);
                setFileList([]);
              } else {
                const requiredHeaders = [
                  "platform",
                  "created_time",
                  "full_name",
                  "phone_number",
                  "city",
                ];
                const actualHeaders = results.meta.fields;
                const missingHeaders = requiredHeaders.filter(
                  (h) => !actualHeaders || !actualHeaders.includes(h)
                );
                if (missingHeaders.length > 0) {
                  message.error(
                    `CSV file is missing required headers: ${missingHeaders.join(
                      ", "
                    )}`
                  );
                  setCsvData([]);
                  setFileList([file]);
                } else {
                  setCsvData(results.data);
                  message.success(`${file.name} file parsed successfully.`);
                  setFileList([file]);
                }
              }
            },
            error: (error) => {
              setLoading(false);
              console.error("CSV Parsing Error:", error);
              message.error("Error parsing CSV file.");
              setCsvData([]);
              setFileList([]);
            },
          });
        } catch (error) {
          setLoading(false);
          console.error("Error reading file:", error);
          message.error("Error reading file.");
          setCsvData([]);
          setFileList([]);
        }
      };
      reader.onerror = () => {
        setLoading(false);
        console.error("FileReader error");
        message.error("Error reading file.");
        setCsvData([]);
        setFileList([]);
      };
      reader.readAsText(file);
      return false;
    },
    onChange(info) {
      let newFileList = info.fileList.slice(-1);
      setFileList(newFileList);
      if (info.file.status === "error") {
        message.error(`${info.file.name} file upload process failed.`);
      }
    },
    onRemove: (file) => {
      setCsvData([]);
      setFileList([]);
      setSelectedRole(null);
    },
  };

  const handleSubmit = async () => {
    if (!selectedRole) {
      message.warning("Please select a role.");
      return;
    }
    if (csvData.length === 0) {
      if (fileList.length > 0) {
        message.warning("Uploaded file could not be processed or is empty.");
      } else {
        message.warning("Please upload and parse a valid CSV file.");
      }
      return;
    }
    setLoading(true);
    try {
      const jsonData = csvData
        .map((row) => {
          if (
            !row.platform ||
            !row.created_time ||
            !row.full_name ||
            !row.phone_number ||
            !row.city
          ) {
            console.warn("Skipping row due to missing data:", row);
            return null;
          }
          let phoneNumber = row.phone_number || "";
          phoneNumber = phoneNumber.replace(/^p:\+/, "");
          return {
            source: row.platform,
            date: row.created_time,
            role: selectedRole,
            fullName: row.full_name,
            phoneNumber: phoneNumber,
            city: row.city,
          };
        })
        .filter((item) => item !== null);

      if (jsonData.length === 0) {
        setLoading(false);
        message.error("No valid data rows found in the CSV to convert.");
        return;
      }
      const response = await addLeads(jsonData);
      setLoading(false);
      if (response && response.error === false) {
        message.success(response.message || "Leads added successfully!");
        setSelectedRole(null);
        setCsvData([]);
        setFileList([]);
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        message.error(
          response?.message || "Failed to add leads. Please try again."
        );
        console.error("API Error:", response);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error during lead submission:", error);
      message.error(
        "An error occurred during submission. Check console for details."
      );
    }
  };

  return (
    <Card className="w-full max-w-lg shadow-lg rounded-lg">
      <Space direction="vertical" size="large" className="w-full">
        <div className="text-center">
          <Title level={3}>Upload Leads CSV</Title>
          <Text type="secondary">Select role and upload the CSV file</Text>
        </div>
        <Select
          placeholder="Select Role"
          style={{ width: "100%" }}
          onChange={handleRoleChange}
          value={selectedRole}
          disabled={loading}
        >
          <Option value="Nurse">Nurse</Option>
          <Option value="Physiotherapist">Physiotherapist</Option>
        </Select>
        <Dragger {...draggerProps} style={{ width: "100%" }} disabled={loading}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag CSV file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Support for a single CSV file. Ensure required headers are present.
          </p>
        </Dragger>
        <Space
          direction="horizontal"
          style={{ width: "100%", justifyContent: "flex-end" }}
        >
          {showBackButton && (
            <Button onClick={onBack} disabled={loading}>
              Back to List
            </Button>
          )}
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={
              !selectedRole ||
              csvData.length === 0 ||
              fileList.length === 0 ||
              loading
            }
            loading={loading}
          >
            {loading ? "Submitting..." : "Submit and Save Leads"}
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

const Leads = () => {
  const [leadsList, setLeadsList] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [viewMode, setViewMode] = useState("loading");
  const [loadingError, setLoadingError] = useState(null);
  const [showBackButtonInUpload, setShowBackButtonInUpload] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [tableScrollY, setTableScrollY] = useState(window.innerHeight - 300);

  useEffect(() => {
    const handleResize = () => {
      setTableScrollY(window.innerHeight - 300);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Filter state
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedConversion, setSelectedConversion] = useState(null);
  const [userListDetails, setUserListDetails] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    setViewMode("loading");
    setLoadingError(null);
    try {
      const response = await getLeads();
      if (response && response.error === false) {
        setLeadsList(response.data || []);
        setFilteredLeads(response.data || []);
        if (response.data && response.data.length > 0) {
          setViewMode("table");
          setShowBackButtonInUpload(true);
        } else {
          setViewMode("upload");
          setShowBackButtonInUpload(false);
        }
      } else {
        throw new Error(response?.message || "Failed to fetch leads");
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
      setLoadingError(error.message);
      setLeadsList([]);
      setFilteredLeads([]);
      setViewMode("upload");
      setShowBackButtonInUpload(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Filtering logic
  useEffect(() => {
    let filtered = leadsList;

    // Role filter
    if (selectedRole) {
      filtered = filtered.filter((lead) => lead.role === selectedRole);
    }

    // Conversion filter
    if (selectedConversion) {
      if (userListDetails.length > 0) {
        const userPhones = userListDetails.map((u) => u.phone);
        console.log(userPhones);
        if (selectedConversion === "Downloaded") {
          filtered = filtered.filter((lead) =>
            userPhones.includes(Number(lead.phoneNumber))
          );
        } else if (selectedConversion === "Not Downloaded") {
          filtered = filtered.filter(
            (lead) => !userPhones.includes(Number(lead.phoneNumber))
          );
        }
      } else if (filterLoading) {
        filtered = [];
      }
    }

    setFilteredLeads(filtered);
  }, [
    leadsList,
    selectedRole,
    selectedConversion,
    userListDetails,
    filterLoading,
  ]);

  // Fetch user list details when conversion filter is selected
  useEffect(() => {
    if (selectedConversion) {
      setFilterLoading(true);
      getUserListDetails().then((res) => {
        setUserListDetails(res.data);
        setFilterLoading(false);
      });
    } else {
      setUserListDetails([]);
    }
  }, [selectedConversion]);

  const handleUploadSuccess = () => {
    fetchLeads();
  };

  const handleClearFilter = () => {
    setSelectedRole(null);
    setSelectedConversion(null);
    setUserListDetails([]);
  };

  const filterMenu = (
    <Menu>
      <Menu.ItemGroup title="Role">
        <Menu.Item key="Nurse" onClick={() => setSelectedRole("Nurse")}>
          Nurse
        </Menu.Item>
        <Menu.Item
          key="Physiotherapist"
          onClick={() => setSelectedRole("Physiotherapist")}
        >
          Physiotherapist
        </Menu.Item>
      </Menu.ItemGroup>
      <Menu.Divider />
      <Menu.ItemGroup title="Conversions">
        <Menu.Item
          key="Downloaded"
          onClick={() => setSelectedConversion("Downloaded")}
        >
          Downloaded
        </Menu.Item>
        <Menu.Item
          key="Not Downloaded"
          onClick={() => setSelectedConversion("Not Downloaded")}
        >
          Not Downloaded
        </Menu.Item>
      </Menu.ItemGroup>
      <Menu.Divider />
      <Menu.Item
        key="clear"
        onClick={handleClearFilter}
        style={{ color: "red" }}
      >
        Clear Filter
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      sorter: (a, b) => a.fullName.localeCompare(b.fullName),
      render: (text) => {
        if (!text) return "N/A";
        const words = text.split(" ");
        const titleCaseWords = words.map((word) => {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        });
        return (
          <span className="text-blue-600">{titleCaseWords.join(" ")}</span>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      sorter: (a, b) => a.role.localeCompare(b.role),
    },
    {
      title: "Phone",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      sorter: (a, b) => a.phoneNumber.localeCompare(b.phoneNumber),
      render: (text) =>
        text ? <span className="text-blue-600">{`+${text}`}</span> : "N/A",
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      sorter: (a, b) => a.city.localeCompare(b.city),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      sorter: (a, b) => a.source.localeCompare(b.source),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
      render: (date) => {
        if (!date) {
          return "N/A";
        }
        const formattedDate = date?.$date
          ? moment(date.$date).format("YYYY-MM-DD")
          : moment(date).format("YYYY-MM-DD");
        return formattedDate !== "Invalid date" ? formattedDate : "N/A";
      },
    },
  ];

  if (viewMode === "loading") {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-5 w-full">
      <Title level={3} style={{ margin: 0, fontSize: 16, paddingBottom: 20 }}>
        Leads ({filteredLeads.length})
      </Title>
      {viewMode === "table" && (
        <>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <Dropdown
                overlay={filterMenu}
                trigger={["click"]}
                placement="bottomLeft"
              >
                <Button icon={<FilterOutlined />}>Filter</Button>
              </Dropdown>
              {/* Show active filters as tags */}
              {selectedRole && (
                <span
                  className="bg-blue-100 text-blue-700 px-2 py-1 rounded mr-2 cursor-pointer flex items-center"
                  onClick={() => setSelectedRole(null)}
                  style={{ fontSize: 12 }}
                >
                  {selectedRole}
                  <span
                    className="ml-1 text-blue-500"
                    style={{ fontWeight: "bold" }}
                  >
                    ×
                  </span>
                </span>
              )}
              {selectedConversion && (
                <span
                  className="bg-green-100 text-green-700 px-2 py-1 rounded mr-2 cursor-pointer flex items-center"
                  onClick={() => setSelectedConversion(null)}
                  style={{ fontSize: 12 }}
                >
                  {selectedConversion}
                  <span
                    className="ml-1 text-green-500"
                    style={{ fontWeight: "bold" }}
                  >
                    ×
                  </span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setViewMode("upload")}
                className="w-25 h-8"
              >
                Add Leads
              </Button>
            </div>
          </div>
          <Table
            dataSource={filteredLeads}
            columns={columns}
            rowKey={(record) => record._id?.$oid}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100"],
              onShowSizeChange: (current, size) => setPageSize(size),
            }}
            size="small"
            scroll={{ y: tableScrollY }}
            rowClassName={() => "custom-table-row"}
            loading={filterLoading}
          />
        </>
      )}

      {viewMode === "upload" && (
        <div className="flex justify-center">
          <UploadLeadsForm
            onUploadSuccess={handleUploadSuccess}
            showBackButton={showBackButtonInUpload}
            onBack={() => setViewMode("table")}
          />
        </div>
      )}

      {loadingError && viewMode !== "loading" && (
        <div className="text-red-500 text-center mt-4">
          Error loading leads: {loadingError}
        </div>
      )}
    </div>
  );
};

export default Leads;
